import { forwardRef, InputHTMLAttributes, ReactNode } from 'react'
import { FieldErrors } from 'react-hook-form'
import FormErrorMessage from './FormErrorMessage'
import { cn } from '../../utils'

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label: ReactNode
  labelClassName?: string
  hideLabel?: boolean
  errors?: FieldErrors
  'data-testid': string
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      id,
      name,
      label,
      labelClassName,
      hideLabel,
      className,
      errors,
      readOnly,
      'data-testid': testId,
      ...props
    },
    ref
  ) => {
    return (
      <div className={className} data-testid={`${testId}-wrapper`}>
        <div>
          <input
            type="checkbox"
            id={id}
            name={name}
            className={cn(
              "elm-checkbox checked:before:content-['×'] checked:before:text-[2rem] checked:before:mt-[-0.5rem] disabled:hover:!bg-current disabled:hover:!border-current print:checked:before:!text-white",
              readOnly &&
                '[&:enabled:hover]:!bg-white/10 [&:enabled:hover:checked]:!bg-cool-gray-700'
            )}
            ref={ref}
            data-testid={testId}
            aria-labelledby={`${id}-label`}
            readOnly={readOnly}
            aria-readonly={readOnly ? 'true' : 'false'}
            tabIndex={readOnly ? -1 : undefined}
            {...props}
          />
          <label
            className={cn(
              'elm-label',
              labelClassName,
              hideLabel && 'sr-only',
              readOnly && 'pointer-events-none !opacity-100 select-none'
            )}
            htmlFor={id}
            id={`${id}-label`}
            data-testid={`${testId}-label`}
          >
            {label}
          </label>
        </div>
        {name && errors && (
          <FormErrorMessage
            errors={errors}
            name={name}
            data-testid={`${testId}-error-message`}
          />
        )}
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'

export default Checkbox
